import { useState } from 'react';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  FileAudio, 
  Copy, 
  CheckCircle, 
  Clock, 
  AlertCircle, 
  Loader2,
  Calendar,
  User,
  AlertTriangle,
  RefreshCw,
  Edit2,
  Save,
  X,
  FileOutput
} from 'lucide-react';
import { Transcription, ActionItem } from '@/types/transcription';
import { ProcessingStatus } from './ProcessingStatus';
import { TranscriptionCostsCard } from './TranscriptionCostsCard';
import { TranscriptionExportModal } from './TranscriptionExportModal';
import { toast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { useTranscriptions } from '@/hooks/useTranscriptions';

interface TranscriptionViewProps {
  transcription: Transcription | null;
  allTranscriptions?: Transcription[];
}

const getStatusBadge = (status: string) => {
  switch (status) {
    case 'completed':
      return { label: 'Voltooid', variant: 'default' as const, icon: CheckCircle, className: 'bg-green-500/20 text-green-500 border-green-500/30' };
    case 'failed':
      return { label: 'Mislukt', variant: 'destructive' as const, icon: AlertCircle, className: '' };
    case 'processing':
    case 'uploading':
    case 'downloading':
    case 'transcribing':
    case 'analyzing':
    case 'analyzing_attempt_1':
    case 'analyzing_attempt_2':
    case 'analyzing_attempt_3':
      return { label: 'Bezig...', variant: 'secondary' as const, icon: Loader2, className: '' };
    default:
      return { label: status, variant: 'outline' as const, icon: Clock, className: '' };
  }
};

const getPriorityBadge = (priority: string) => {
  switch (priority) {
    case 'high':
      return { label: 'Hoog', className: 'bg-red-500/20 text-red-500 border-red-500/30' };
    case 'medium':
      return { label: 'Medium', className: 'bg-yellow-500/20 text-yellow-500 border-yellow-500/30' };
    case 'low':
      return { label: 'Laag', className: 'bg-green-500/20 text-green-500 border-green-500/30' };
    default:
      return { label: priority, className: '' };
  }
};

// SummaryCard component with retry functionality
const SummaryCard = ({ transcription }: { transcription: Transcription }) => {
  const { retryAnalysis } = useTranscriptions();
  const isAnalysisMissing = transcription.summary?.includes('niet beschikbaar');
  const isRetrying = retryAnalysis.isPending;
  
  const handleRetry = () => {
    retryAnalysis.mutate(transcription.id);
  };
  
  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">Samenvatting</CardTitle>
          {isAnalysisMissing && (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleRetry}
              disabled={isRetrying}
            >
              {isRetrying ? (
                <>
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  Analyseren...
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4 mr-1" />
                  Opnieuw Analyseren
                </>
              )}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {isAnalysisMissing ? (
          <div className="flex items-start gap-2 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
            <AlertTriangle className="h-5 w-5 text-yellow-500 shrink-0" />
            <div>
              <p className="text-sm font-medium text-yellow-600">Analyse niet beschikbaar</p>
              <p className="text-sm text-muted-foreground mt-1">
                De transcriptie is succesvol, maar de AI-analyse is mislukt. 
                Klik op "Opnieuw Analyseren" om het nogmaals te proberen.
              </p>
            </div>
          </div>
        ) : (
          <p className="text-sm">{transcription.summary}</p>
        )}
      </CardContent>
    </Card>
  );
};

export const TranscriptionView = ({ transcription, allTranscriptions = [] }: TranscriptionViewProps) => {
  const { updateTranscription } = useTranscriptions();
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editDate, setEditDate] = useState('');
  const [editExtraInfo, setEditExtraInfo] = useState('');
  const [showExportModal, setShowExportModal] = useState(false);

  if (!transcription) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">
          <FileAudio className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p>Selecteer een transcriptie uit de geschiedenis of upload een nieuw audiobestand.</p>
        </CardContent>
      </Card>
    );
  }

  const statusInfo = getStatusBadge(transcription.status);
  const StatusIcon = statusInfo.icon;
  const isProcessing = !['completed', 'failed'].includes(transcription.status);

  const displayTitle = transcription.title || transcription.filename;

  const startEditing = () => {
    setEditTitle(transcription.title || transcription.filename);
    setEditDate(transcription.conversation_date || '');
    setEditExtraInfo(transcription.extra_info || '');
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setIsEditing(false);
  };

  const saveEditing = async () => {
    try {
      await updateTranscription.mutateAsync({
        id: transcription.id,
        title: editTitle,
        conversation_date: editDate || null,
        extra_info: editExtraInfo || null,
      });
      setIsEditing(false);
    } catch (error) {
      // Error handled in hook
    }
  };

  const copyTranscript = () => {
    if (transcription.transcript) {
      navigator.clipboard.writeText(transcription.transcript);
      toast({
        title: "Gekopieerd",
        description: "Transcript is gekopieerd naar het klembord.",
      });
    }
  };

  return (
    <div className="space-y-4">
      {/* Top Row: Header + Costs */}
      <div className="flex gap-4">
        {/* Header Card */}
        <Card className="flex-1">
          <CardHeader className="pb-3">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3 flex-1">
                <FileAudio className="h-6 w-6 text-primary shrink-0 mt-1" />
                <div className="flex-1 space-y-2">
                  {isEditing ? (
                    <>
                      <div>
                        <label className="text-xs text-muted-foreground">Titel</label>
                        <Input
                          value={editTitle}
                          onChange={(e) => setEditTitle(e.target.value)}
                          placeholder="Naam van de transcriptie"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground">Datum gesprek</label>
                        <Input
                          type="date"
                          value={editDate}
                          onChange={(e) => setEditDate(e.target.value)}
                        />
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground">Extra informatie</label>
                        <Textarea
                          value={editExtraInfo}
                          onChange={(e) => setEditExtraInfo(e.target.value)}
                          placeholder="Context, deelnemers, locatie..."
                          rows={2}
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      <CardTitle className="text-lg">{displayTitle}</CardTitle>
                      <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                        <span>
                          Geüpload: {format(new Date(transcription.created_at), "d MMM yyyy 'om' HH:mm", { locale: nl })}
                        </span>
                        {transcription.conversation_date && (
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            Gesprek: {format(new Date(transcription.conversation_date), "d MMM yyyy", { locale: nl })}
                          </span>
                        )}
                      </div>
                      {transcription.extra_info && (
                        <p className="text-sm text-muted-foreground italic">
                          {transcription.extra_info}
                        </p>
                      )}
                    </>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {isEditing ? (
                  <>
                    <Button size="sm" variant="outline" onClick={cancelEditing}>
                      <X className="h-4 w-4" />
                    </Button>
                    <Button size="sm" onClick={saveEditing} disabled={updateTranscription.isPending}>
                      {updateTranscription.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Save className="h-4 w-4" />
                      )}
                    </Button>
                  </>
                ) : (
                  <Button size="sm" variant="ghost" onClick={startEditing}>
                    <Edit2 className="h-4 w-4" />
                  </Button>
                )}
                <Badge 
                  variant={statusInfo.variant}
                  className={cn("flex items-center gap-1", statusInfo.className)}
                >
                  <StatusIcon className={cn("h-3 w-3", isProcessing && "animate-spin")} />
                  {statusInfo.label}
                </Badge>
              </div>
            </div>
          </CardHeader>
          {isProcessing && (
            <CardContent className="pt-0">
              <ProcessingStatus 
                status={transcription.status} 
                errorMessage={transcription.error_message} 
              />
            </CardContent>
          )}
        </Card>

        {/* Costs Card */}
        <TranscriptionCostsCard 
          currentTranscription={transcription} 
          transcriptions={allTranscriptions} 
        />
      </div>

      {/* Export Button (when completed) */}
      {transcription.status === 'completed' && transcription.summary && !transcription.summary.includes('niet beschikbaar') && (
        <div className="flex justify-end">
          <Button variant="outline" onClick={() => setShowExportModal(true)}>
            <FileOutput className="h-4 w-4 mr-2" />
            Opslaan naar Project
          </Button>
        </div>
      )}

      {/* Transcript Card - Always show if available */}
      {transcription.transcript && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Volledige Transcriptie</CardTitle>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">
                  {transcription.transcript.length.toLocaleString()} tekens
                </span>
                <Button variant="outline" size="sm" onClick={copyTranscript}>
                  <Copy className="h-4 w-4 mr-1" />
                  Kopieer
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[400px] w-full rounded-md border p-4">
              <p className="text-sm whitespace-pre-wrap">{transcription.transcript}</p>
            </ScrollArea>
          </CardContent>
        </Card>
      )}

      {/* Summary Card */}
      {transcription.status === 'completed' && (
        <SummaryCard transcription={transcription} />
      )}

      {/* Action Items Card */}
      {transcription.status === 'completed' && transcription.action_items.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">
              Actielijst ({transcription.action_items.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {transcription.action_items.map((item: ActionItem, index: number) => {
                const priorityInfo = getPriorityBadge(item.priority);
                return (
                  <div 
                    key={index} 
                    className="p-3 border rounded-lg space-y-2"
                  >
                    <p className="font-medium">{item.task}</p>
                    <div className="flex flex-wrap items-center gap-2 text-sm">
                      <div className="flex items-center gap-1 text-muted-foreground">
                        <Calendar className="h-3 w-3" />
                        {item.deadline}
                      </div>
                      <Badge variant="outline" className={priorityInfo.className}>
                        {priorityInfo.label}
                      </Badge>
                      {item.speaker && (
                        <div className="flex items-center gap-1 text-muted-foreground">
                          <User className="h-3 w-3" />
                          {item.speaker}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Empty action items message */}
      {transcription.status === 'completed' && transcription.action_items.length === 0 && (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            <p>Geen actiepunten gevonden in deze transcriptie.</p>
          </CardContent>
        </Card>
      )}

      {/* Export Modal */}
      <TranscriptionExportModal
        open={showExportModal}
        onOpenChange={setShowExportModal}
        transcription={transcription}
      />
    </div>
  );
};
